import { useMemo } from 'react';
import { buildFeedModel, type FeedModel } from '../selectors/feed';
import { combine, useLogFailure } from './combine';
import { useDataClock } from './DataProvider';
import {
  useBlastRadiusScores,
  useBusinessElements,
  useInfrastructureElements,
  useRemediations,
  useThreatCandidates,
  useThreatElements,
  useThreatEvents,
} from './hooks';

const EMPTY_FEED: FeedModel = { rows: [], suppressed: [] };

export interface FeedData extends FeedModel {
  /** Nothing to show yet: the first read, or a read again after a failure. */
  loading: boolean;
  /** A read failed. The rows are empty, as the feed has always done on a failed read. */
  error: boolean;
  /** Shows the latest records and reads again now. */
  refresh: () => Promise<void>;
}

/** The Threat Feed's rows and suppressed alerts, built from the shared cache. */
export function useFeedData(): FeedData {
  const { refreshNow } = useDataClock();
  const elements = useThreatElements();
  const threatEvents = useThreatEvents();
  const candidates = useThreatCandidates();
  const remediations = useRemediations();
  const infrastructure = useInfrastructureElements();
  const business = useBusinessElements();
  const blastScores = useBlastRadiusScores();
  const state = combine([
    elements,
    threatEvents,
    candidates,
    remediations,
    infrastructure,
    business,
    blastScores,
  ]);

  const model = useMemo(() => {
    if (state.failed || !state.ready) return EMPTY_FEED;
    return buildFeedModel({
      threatEvents: threatEvents.data ?? [],
      candidates: candidates.data ?? [],
      elements: elements.data ?? [],
      remediations: remediations.data ?? [],
      infrastructure: infrastructure.data ?? [],
      business: business.data ?? [],
      blastScores: blastScores.data ?? [],
    });
    // The records themselves: a read that brings back the same ones gives the same references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.failed,
    state.ready,
    threatEvents.data,
    candidates.data,
    elements.data,
    remediations.data,
    infrastructure.data,
    business.data,
    blastScores.data,
  ]);

  useLogFailure('Threat feed could not be loaded', [
    elements,
    threatEvents,
    candidates,
    remediations,
    infrastructure,
    business,
    blastScores,
  ]);

  return { ...model, loading: state.loading, error: state.error, refresh: refreshNow };
}
